import { movies } from '@/mocks/movies';
import { tvShows } from '@/mocks/tvShows';
import { varietyShows } from '@/mocks/varietyShows';
import type { Work, WorkType, Region, SubtitleStatus } from '@/types/work';

export const allWorks = [
  ...movies,
  ...tvShows,
  ...varietyShows,
] as unknown as Work[];

export const TYPE_META: Record<
  WorkType,
  { label: string; en: string; description: string; icon: string }
> = {
  movie: {
    label: '电影',
    en: 'Movie',
    description: '日韩院线电影与独立佳作的中文字幕资源，覆盖公映版与蓝光版。',
    icon: 'ri-film-line',
  },
  tv: {
    label: '电视剧',
    en: 'TV Series',
    description: '日韩剧集整季中文字幕追踪，实时同步更新与制作进度。',
    icon: 'ri-tv-2-line',
  },
  variety: {
    label: '综艺',
    en: 'Variety',
    description: '热门日韩综艺节目字幕合集，含规则说明与流行语注释。',
    icon: 'ri-mic-line',
  },
};

export const REGION_OPTIONS: { value: Region; label: string }[] = [
  { value: 'jp', label: '日本' },
  { value: 'kr', label: '韩国' },
];

export const STATUS_OPTIONS: { value: SubtitleStatus; label: string }[] = [
  { value: 'completed', label: '已完成' },
  { value: 'in-progress', label: '制作中' },
];

export function getWorkById(id: string): Work | undefined {
  return allWorks.find((work) => work.id === id);
}

export function getWorksByType(type: WorkType): Work[] {
  return allWorks.filter((work) => work.type === type);
}

export function getLatestWorks(limit = 8, type?: WorkType): Work[] {
  const pool = type ? getWorksByType(type) : allWorks;
  return [...pool]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, limit);
}

export function getPopularWorks(limit = 6, type?: WorkType): Work[] {
  const pool = type ? getWorksByType(type) : allWorks;
  return [...pool].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
}

export function getRelatedWorks(work: Work, limit = 4): Work[] {
  const sameType = allWorks.filter(
    (item) => item.id !== work.id && item.type === work.type
  );
  const scored = sameType.map((item) => {
    const tagScore = item.tags.filter((tag) => work.tags.includes(tag)).length;
    const regionScore = item.region === work.region ? 1 : 0;
    return { item, score: tagScore * 2 + regionScore };
  });
  scored.sort((a, b) => b.score - a.score || b.item.popularity - a.item.popularity);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function searchWorks(keyword: string): Work[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return allWorks.filter((work) => {
    return (
      work.title.toLowerCase().includes(key) ||
      work.originalTitle.toLowerCase().includes(key) ||
      work.genre.toLowerCase().includes(key) ||
      work.tags.some((tag) => tag.toLowerCase().includes(key))
    );
  });
}

export interface WorkFilters {
  type?: WorkType;
  region?: Region;
  yearFrom?: number;
  yearTo?: number;
  status?: SubtitleStatus;
  sort?: 'updated' | 'popular' | 'year';
}

export function filterWorks(source: Work[], filters: WorkFilters): Work[] {
  const result = source.filter((work) => {
    if (filters.type && work.type !== filters.type) return false;
    if (filters.region && work.region !== filters.region) return false;
    if (filters.status && work.subtitleStatus !== filters.status) return false;
    if (filters.yearFrom && work.year < filters.yearFrom) return false;
    if (filters.yearTo && work.year > filters.yearTo) return false;
    return true;
  });
  const sort = filters.sort || 'updated';
  result.sort((a, b) => {
    if (sort === 'popular') return b.popularity - a.popularity;
    if (sort === 'year') return b.year - a.year || b.popularity - a.popularity;
    return b.updatedAt.localeCompare(a.updatedAt);
  });
  return result;
}

export function formatDate(value: string): string {
  return value.replace(/-/g, '.');
}

export function formatPopularity(value: number): string {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}w`;
  return `${(value / 1000).toFixed(1)}k`;
}

export function getYearOptions(): number[] {
  const years = new Set<number>();
  allWorks.forEach((work) => years.add(work.year));
  return Array.from(years).sort((a, b) => b - a);
}